import 'rsuite/dist/rsuite-no-reset.min.css'
import 'rsuite/DatePicker/styles/index.css'

import { zodResolver } from '@hookform/resolvers/zod'
import { Cross1Icon as CloseIcon, InfoCircledIcon } from '@radix-ui/react-icons'
import { message } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { StyledFormInput } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'features/rightDrawer/utils/RightDrawerProvider'
import { useRolodexEntries } from 'features/rolodex'
import React, { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { CustomProvider, DatePicker, SelectPicker } from 'rsuite'
import styled from 'styled-components'
import { GeckoMeasurementUnit, RolodexEntryType } from 'types'

import { FileSchema } from './editFileSchema'
import { ParserType } from './utils/enums'
import { uploadFileAndMetaData } from './utils/uploadFileAndMetaData'

const StyledUploadForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledIcon = styled(InfoCircledIcon)`
  color: ${({ theme }) => theme.forecast.icon1};
  margin-right: 8px;
  cursor: pointer;
  position: relative;
`

interface TooltipProps {
  visible: boolean
}

//use antd tooltip instead of rsuite tooltip

const Tooltip = styled.div<TooltipProps>`
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  background-color: #555;
  color: white;
  text-align: center;
  border-radius: 4px;
  padding: 10px;
  position: absolute;
  width: 350px;
  z-index: 2;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition:
    opacity 0.3s,
    visibility 0.3s;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
  white-space: normal;
  word-wrap: break-word;
`

const FileDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`

const FileName = styled.span`
  word-wrap: break-word;
`
const StyledInput = styled.input`
  display: none;
`
const StyledButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  height: 48px;
  cursor: pointer;
  border-radius: 4px;
  margin-top: 16px;

  &:disabled {
    background-color: #007bff;
    cursor: not-allowed;
    opacity: 0.6;
  }
`
const LabelInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledOffsetInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
`

const StyledFileLabel = styled.label`
  flex-grow: 1;
  flex-basis: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border: none;
  border-radius: 4px;
  margin-right: 12px;
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
`
export const GeckoHHUTFileProps: RightSideDrawerProps = {
  width: '30%',
}

type UploadHHUTFileComponentProps = {
  siteID: string | undefined
  unitID: string | undefined
}

export const UploadHHUTFile: React.FC<UploadHHUTFileComponentProps> = ({
  siteID,
  unitID,
}) => {
  const [selectedUnitID, setSelectedUnitID] = useState<string | null>(
    unitID ?? null
  )

  const { units } = useRolodexEntries({
    type: RolodexEntryType.UNIT,
    tags: { site: siteID ?? '' },
  })

  const { components } = useRolodexEntries({
    type: RolodexEntryType.COMPONENT,
    tags: { site: siteID ?? '', unit: selectedUnitID ?? '' },
  })

  const parserOptions = [
    { label: 'Default', value: ParserType.Default },
    { label: 'GP - Brewton Parser', value: ParserType.GP_Brewton },
    { label: 'Falcon - H0 Parser', value: ParserType.FALCON_H0 },
  ]

  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [selectedInspection, setSelectedInspection] = useState<
    string | undefined
  >()
  const [_, setInspectionDate] = useState<string | null>(null) // eslint-disable-line @typescript-eslint/no-unused-vars
  const { closeDrawer } = useRightDrawer()
  const form = useForm({
    resolver: zodResolver(FileSchema),
    mode: 'onChange',
    defaultValues: {
      unitID: unitID ?? '',
      component: '',
      inspection: '',
      date: '',
      x_offset: 0,
      y_offset: 0,
      x_unit: GeckoMeasurementUnit.Degrees.id,
      y_unit: GeckoMeasurementUnit.Feet.id,
      binWidth: undefined,
      binHeight: undefined,
    },
  })

  const selectOptions = components?.map((c) => ({
    label: c.data.name,
    value: c.id,
  }))

  const onDateChange = (date: Date): string => {
    const dateFieldArray = new Date(date).toISOString().split('T')
    return dateFieldArray[0]
  }
  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }
    const formattedDate = new Intl.DateTimeFormat('en-GB', options).format(date)
    const [day, month, year] = formattedDate.split(' ')
    return `${day} ${month}, ${year}`
  }

  const handleMainSave = async (data: FileSchema) => {
    try {
      const {
        component,
        date,
        files,
        x_offset,
        y_offset,
        x_unit,
        y_unit,
        inspection,
        parserType,
        binWidth,
        binHeight,
      } = data
      const selectedFile = files[0]
      if (selectedFile && component) {
        const selectedInspection = inspections?.find((i) => i.id === inspection)
        const job = selectedInspection?.tags?.job || ''
        const adjustedDate = selectedInspection
          ? onDateChange(new Date(selectedInspection.data.date))
          : onDateChange(new Date(date ?? ''))

        const metaData = {
          component,
          date: adjustedDate,
          x_offset,
          y_offset,
          x_unit,
          y_unit,
          inspection:
            inspection === CREATE_NEW_INSPECTION_VALUE ? undefined : inspection,
          job,
          parserType,
          binWidth,
          binHeight,
        }

        await uploadFileAndMetaData(
          selectedFile,
          metaData,
          RISK_CALCULATOR_URL,
          axios
        ).then(() => {
          closeDrawer()
          return message.success('File and metadata uploaded successfully')
        })
      } else {
        alert('Please select a file first and fill in all required fields')
      }
    } catch (error) {
      const errorMessage =
        (error as Error)?.message || 'An unknown error occurred'
      message.error(`Form submission failed: Error parsing - ${errorMessage}`)
    }
  }
  const { handleSubmit, control } = form

  const onSubmit = async (data: FileSchema) => {
    await handleMainSave(data)
  }

  const unitOptionsforX = [
    { label: 'Degrees', value: GeckoMeasurementUnit.Degrees.id },
    { label: 'Feet', value: GeckoMeasurementUnit.Feet.id },
    { label: 'Meters', value: GeckoMeasurementUnit.Meters.id },
  ]
  const unitOptionsforY = [
    { label: 'Feet', value: GeckoMeasurementUnit.Feet.id },
    { label: 'Meters', value: GeckoMeasurementUnit.Meters.id },
  ]

  const selectedComponent = form.watch('component')
  const unitOptions = units?.map((unit) => ({
    label: unit.data.name,
    value: unit.id,
  }))

  const { inspections } = useRolodexEntries({
    type: RolodexEntryType.INSPECTION,
    tags: { site: siteID ?? '', component: selectedComponent ?? '' },
  })

  const CREATE_NEW_INSPECTION_VALUE = 'Create New Inspection'

  const selectOptionsInspections = [
    { label: CREATE_NEW_INSPECTION_VALUE, value: CREATE_NEW_INSPECTION_VALUE },
    ...(inspections ?? []).map((i) => ({
      label: formatDate(i.data.date),
      value: i.id,
    })),
  ]

  const StyledDatePickerWrapper = styled.div<{ isDisabled: boolean }>`
    opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
    pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};

    .rs-picker-toggle {
      background-color: ${({ isDisabled }) =>
        isDisabled ? '#e0e0e0' : 'black'};
    }
  `
  useEffect(() => {
    if (selectedInspection) {
      const selectedInspectionEntry = inspections?.find(
        (i) => i.id === selectedInspection
      )
      if (selectedInspectionEntry) {
        const date = new Date(selectedInspectionEntry.data.date)
        setInspectionDate(date.toISOString())
      }
    } else {
      setInspectionDate(null)
    }
  }, [selectedInspection, inspections])

  return (
    <FormProvider {...form}>
      <StyledUploadForm
        onSubmit={handleSubmit(
          (data) => {
            onSubmit(data)
          },
          (e) => {
            const errorMessages = [
              e.component?.message,
              e.date?.message,
              e.files?.message,
              e.x_offset?.message,
              e.y_offset?.message,
              e.x_unit?.message,
              e.y_unit?.message,
              e.inspection?.message,
            ]
              .filter((d) => d)
              .join(',')

            message.error(`Validation error: ${errorMessages}`)
          }
        )}>
        <LabelInputWrapper>
          <label htmlFor='parser'>Parser Type</label>
          <Controller
            name='parserType'
            control={control}
            render={({ field }) => (
              <SelectPicker
                {...field}
                data={parserOptions}
                style={{ width: '100%' }}
                searchable={false}
                cleanable={false}
                placeholder='Select a parser type'
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
        </LabelInputWrapper>
        <LabelInputWrapper>
          <label htmlFor='unit'>Select Asset</label>
          <Controller
            name='unitID'
            control={control}
            render={({ field }) => (
              <SelectPicker
                {...field}
                data={unitOptions}
                style={{ width: '100%' }}
                searchable
                placeholder='Select an asset'
                onChange={(value) => {
                  field.onChange(value)
                  setSelectedUnitID(value)
                }}
              />
            )}
          />
        </LabelInputWrapper>

        <LabelInputWrapper>
          <label htmlFor='component'>Component Type</label>
          <Controller
            name='component'
            control={control}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <CustomProvider theme='dark'>
                <SelectPicker
                  data-testid='component-combobox'
                  ref={ref}
                  onBlur={onBlur}
                  onChange={(value) => {
                    onChange(value)
                  }}
                  value={value}
                  searchable={true}
                  data={selectOptions}
                  disabled={!selectedUnitID}
                  style={{ width: '100%' }}
                />
              </CustomProvider>
            )}
          />
        </LabelInputWrapper>
        <LabelInputWrapper>
          <label htmlFor='inspection'>Select Inspection</label>
          <Controller
            name='inspection'
            control={control}
            render={({ field }) => (
              <SelectPicker
                {...field}
                data-testid='inspection-picker'
                data={selectOptionsInspections}
                style={{ width: '100%' }}
                searchable
                onChange={(value) => {
                  field.onChange(value)
                  setSelectedInspection(value ?? undefined)
                }}
                disabled={!selectedComponent}
              />
            )}
          />
        </LabelInputWrapper>
        <LabelInputWrapper>
          <StyledDatePickerWrapper
            isDisabled={selectedInspection !== CREATE_NEW_INSPECTION_VALUE}>
            <label htmlFor='date'>Inspection Date</label>
            <Controller
              name='date'
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format='yyyy-MM-dd'
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => {
                    if (date) {
                      field.onChange(date.toISOString())
                    }
                  }}
                  block
                />
              )}
              disabled={selectedInspection !== CREATE_NEW_INSPECTION_VALUE}
            />
          </StyledDatePickerWrapper>
        </LabelInputWrapper>

        <CustomProvider theme='dark'>
          <StyledOffsetInputWrapper>
            <label htmlFor='x_offset'>
              Distance from the zero-line (X Offset)
            </label>
            <Controller
              name='x_unit'
              control={control}
              render={({ field }) => (
                <CustomProvider theme='dark'>
                  <SelectPicker
                    {...field}
                    data={unitOptionsforX}
                    cleanable={false}
                    searchable={false}
                    style={{ width: '100%' }}
                    placeholder='Select Unit'
                  />
                </CustomProvider>
              )}
            />
            <Controller
              name='x_offset'
              control={control}
              render={({ field }) => (
                <StyledFormInput
                  type='number'
                  {...field}
                  step='any'
                  style={{ width: '100%' }}
                />
              )}
            />
          </StyledOffsetInputWrapper>

          <StyledOffsetInputWrapper>
            <label htmlFor='y_offset'>
              {"Distance from component's bottom edge (Y offset)"}
            </label>
            <Controller
              name='y_unit'
              control={control}
              render={({ field }) => (
                <CustomProvider theme='dark'>
                  <SelectPicker
                    {...field}
                    data={unitOptionsforY}
                    cleanable={false}
                    searchable={false}
                    style={{ width: '100%' }}
                    placeholder='Select Unit'
                  />
                </CustomProvider>
              )}
            />
            <Controller
              name='y_offset'
              control={control}
              render={({ field }) => (
                <StyledFormInput
                  type='number'
                  {...field}
                  step='any'
                  style={{ width: '100%' }}
                />
              )}
            />
          </StyledOffsetInputWrapper>

          <StyledOffsetInputWrapper>
            <label htmlFor='binWidth'>Bin Width (Optional)</label>
            <Controller
              name='binWidth'
              control={control}
              render={({ field }) => (
                <StyledFormInput
                  type='number'
                  {...field}
                  step='any'
                  style={{ width: '100%' }}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                    field.onChange(value === '' ? undefined : Number(value))
                  }}
                />
              )}
            />
          </StyledOffsetInputWrapper>

          <StyledOffsetInputWrapper>
            <label htmlFor='binHeight'>Bin Height (Optional)</label>
            <Controller
              name='binHeight'
              control={control}
              render={({ field }) => (
                <StyledFormInput
                  type='number'
                  {...field}
                  step='any'
                  style={{ width: '100%' }}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                    field.onChange(value === '' ? undefined : Number(value))
                  }}
                />
              )}
            />
          </StyledOffsetInputWrapper>
        </CustomProvider>

        <Controller
          name='files'
          control={control}
          render={({ field }) => (
            <FileUploadContainer>
              <StyledInput
                type='file'
                accept='.xls,.xlsx'
                id='file-upload'
                onChange={(e) => {
                  field.onChange(e.target.files)
                }}
              />
              {!field.value && (
                <StyledFileLabel htmlFor='file-upload'>
                  Choose File
                </StyledFileLabel>
              )}
              {field.value && (
                <div>
                  <FileDisplayContainer
                    onClick={() => {
                      field.onChange(undefined)
                    }}>
                    <FileName>{field.name}</FileName>
                    <CloseIcon />
                  </FileDisplayContainer>
                </div>
              )}
              <StyledIcon
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
              />
              <Tooltip visible={tooltipVisible}>
                Please upload the file in .xls/.xlsx format. Ensure the
                elevations are on the left of the readings. Ensure the readings
                are on the first sheet of the xls.
              </Tooltip>
            </FileUploadContainer>
          )}
        />
        <StyledButton type='submit'>Upload File</StyledButton>
      </StyledUploadForm>
    </FormProvider>
  )
}
